import { useJdmResetPasswordMutation } from '../../apis/jdmPasswordApi';
import { useUsername } from '../useUsersSlice';
import { useCallback, useEffect, useState } from 'react';
import { AlertType, TowerAlert } from '../../../../common/Alerts';

const extractErrorMessage = (error: unknown, fallbackMessage: string) => {
    const errorData = (error as any)?.data;

    if (typeof errorData === 'string') {
        const match = errorData.match(/"([^"]+)"/);
        return match ? match[1] : fallbackMessage;
    }

    if (typeof errorData?.message === 'string' && errorData.message.trim()) {
        return errorData.message;
    }

    return fallbackMessage;
};

const useResetPassword = () => {
    const { username } = useUsername();

    const [resetPasswordMutation, { data, isLoading, isError, isSuccess, error, reset: resetMutation }] =
        useJdmResetPasswordMutation();

    const [alerts, setAlerts] = useState<TowerAlert[]>([]);

    // show error alerts
    useEffect(() => {
        if (isError) {
            const status = (error as any)?.status;
            const originalStatus = (error as any)?.originalStatus;
            let message = 'Password reset failed. Please try again.';

            if (status === 400 || originalStatus === 400) {
                message = extractErrorMessage(error, 'Invalid request. Please check your password.');
            } else if (status === 409 || originalStatus === 409) {
                message =
                    'Cannot reset JDM password: your JDM username matches your ForeFlight username. Please use ForeFlight password reset.';
            }

            setAlerts([{ type: AlertType.error, message }]);
        }
    }, [isError, error]);

    const resetPassword = useCallback(
        (password: string, code: string) => {
            if (isLoading) return;

            setAlerts([]);
            resetPasswordMutation({
                username,
                newPassword: password,
                code,
            });
        },
        [isLoading, resetPasswordMutation, username]
    );

    const reset = useCallback(() => {
        setAlerts([]);
        resetMutation();
    }, [resetMutation]);

    return {
        resetPassword,
        isLoading,
        isSuccess,
        alerts,
        reset,
    };
};

export default useResetPassword;
